class PredictiveSearch extends HTMLElement {
    constructor() {
        super();
        this.modal = this.closest('.search-modal');
        this.cachedResults = {};
        const perPage = this.dataset.perPage || 5;
        this.input = this.querySelector('input[type="search"]');
        this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
        this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
        this.isOpen = false;
        this.searchTerm = '';
        this.setupEventListeners();
    }
    setupEventListeners() {
        if (this.querySelector('form.search')) {
            this.querySelector('form.search').addEventListener('submit', this.onFormSubmit.bind(this));
        }
        if (this.querySelector('button[type="reset"]')) {
            this.querySelector('button[type="reset"]').addEventListener('click', this.clear.bind(this));
        }
        if (this.input) {
            this.input.addEventListener('input', debounce((event) => {
                this.onChange(event);
            }, 300).bind(this));
            this.input.addEventListener('focus', this.onFocus.bind(this));
        }
        this.addEventListener('focusout', this.onFocusOut.bind(this));
        this.addEventListener('keyup', this.onKeyup.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));
    }
    getQuery() {
        return this.input ? this.input.value.trim() : '';
    }
    onChange() {
        const newSearchTerm = this.getQuery();
        if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
            this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
        }
        this.updateSearchForTerm(this.searchTerm, newSearchTerm);
        this.searchTerm = newSearchTerm;
        if (!this.searchTerm.length) {
            this.clear();
            this.close(true);
            return;
        }
        this.getSearchResults(this.searchTerm);
    }
    onFormSubmit(event) {
        if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    }
    onFocus() {
        const currentSearchTerm = this.getQuery();
        if (!currentSearchTerm.length) return;
        if (this.searchTerm !== currentSearchTerm) {
            this.onChange();
        } else if (this.getAttribute('results') === 'true') {
            this.open();
        } else {
            this.getSearchResults(this.searchTerm);
        }
    }
    onFocusOut() {
        setTimeout(() => {
            if (!this.contains(document.activeElement)) this.close();
        });
    }
    onKeyup(event) {
        if (!this.getQuery().length) {
            this.clear(event);
            this.close(true);
        }
        event.preventDefault();
        switch (event.code) {
            case 'ArrowUp':
                this.switchOption('up');
                break;
            case 'ArrowDown':
                this.switchOption('down');
                break;
            case 'Enter':
                this.selectOption();
                break;
        }
    }
    onKeydown(event) {
        if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
            event.preventDefault();
        }
    }
    updateSearchForTerm(previousTerm, newTerm) {
        const searchForTextElement = this.querySelector("[data-predictive-search-search-for-text]");
        const currentButtonText = searchForTextElement?.innerText;
        if (currentButtonText) {
            const newButtonText = currentButtonText.replace(previousTerm, newTerm);
            searchForTextElement.innerText = newButtonText;
        }
    }
    switchOption(direction) {
        if (!this.getAttribute('open')) return;
        const moveUp = direction === 'up';
        const selectedElement = this.querySelector('[aria-selected="true"]');
        const allElements = this.querySelectorAll('li');
        let activeElement = this.querySelector('li');
        if (moveUp && !selectedElement) return;
        if (this.statusElement) this.statusElement.textContent = '';
        if (!moveUp && selectedElement) {
            activeElement = selectedElement.nextElementSibling || allElements[0];
        } else if (moveUp) {
            activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
        }
        if (activeElement === selectedElement) return;
        if (activeElement) activeElement.setAttribute('aria-selected', true);
        if (selectedElement) selectedElement.setAttribute('aria-selected', false);
        if (activeElement) {
            this.setLiveRegionText(activeElement.textContent);
            if (this.input) this.input.setAttribute('aria-activedescendant', activeElement.id);
        }
    }
    selectOption() {
        const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
        if (selectedProduct) selectedProduct.click();
    }
    getSearchResults(searchTerm) {
        const queryKey = searchTerm.replace(" ", "-").toLowerCase();
        const perPage = this.dataset.perPage || 5;
        this.setLiveRegionLoadingState();
        if (this.cachedResults[queryKey]) {
            this.renderSearchResults(this.cachedResults[queryKey]);
            return;
        }
        const searchUrl = `${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,query,collection,page,article&${encodeURIComponent('resources[limit]')}=${perPage}&${encodeURIComponent('resources[limit_scope]')}=each&${encodeURIComponent('resources[options][fields]')}=${encodeURIComponent('title,product_type,variants.title,vendor,variants.sku')}&section_id=predictive-search`;
        fetch(searchUrl).then((response) => {
            if (!response.ok) {
                var error = new Error(response.status);
                this.close();
                throw error;
            }
            return response.text();
        }).then((text) => {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const sectionNode = doc.querySelector('#shopify-section-predictive-search') || doc.querySelector('[id*="predictive-search"]') || doc.body;
            const resultsMarkup = sectionNode ? sectionNode.innerHTML : '';
            this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
                predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
            });
            this.renderSearchResults(resultsMarkup);
        }).catch((error) => {
            if (error?.code === 20) {
                return;
            }
            this.close();
        });
    }
    setLiveRegionLoadingState() {
        this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
        this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
        if (this.loadingText) this.setLiveRegionText(this.loadingText);
        this.setAttribute('loading', true);
    }
    setLiveRegionText(statusText) {
        if (!this.statusElement) return;
        this.statusElement.setAttribute('aria-hidden', 'false');
        this.statusElement.textContent = statusText;
        setTimeout(() => {
            this.statusElement.setAttribute('aria-hidden', 'true');
        }, 1000);
    }
    renderSearchResults(resultsMarkup) {
        if (this.predictiveSearchResults) {
            this.predictiveSearchResults.innerHTML = resultsMarkup;
        }
        this.setAttribute('results', true);
        this.setLiveRegionResults();
        this.open();
        const searchModalContainer = this.closest('.search-modal') || this.closest('.header_search_item') || this.parentElement;
        if (searchModalContainer) {
            searchModalContainer.classList.add('searching');
        }
    }
    setLiveRegionResults() {
        this.removeAttribute('loading');
        const countNode = this.querySelector('[data-predictive-search-live-region-count-value]');
        if (countNode) {
            this.setLiveRegionText(countNode.textContent);
        }
    }
    getResultsMaxHeight() {
        const headerEl = document.querySelector('.header');
        if (headerEl) {
            this.resultsMaxHeight = window.innerHeight - headerEl.getBoundingClientRect().bottom;
        } else {
            this.resultsMaxHeight = 450;
        }
        return this.resultsMaxHeight;
    }
    open() {
        if (this.predictiveSearchResults) {
            this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
        }
        this.setAttribute('open', true);
        if (this.input) this.input.setAttribute('aria-expanded', true);
        this.isOpen = true;
    }
    close(clearSearchTerm = false) {
        this.closeResults(clearSearchTerm);
        this.isOpen = false;
    }
    closeResults(clearSearchTerm = false) {
        if (clearSearchTerm && this.input) {
            this.input.value = '';
            this.removeAttribute('results');
        }
        const selected = this.querySelector('[aria-selected="true"]');
        if (selected) selected.setAttribute('aria-selected', false);
        if (this.input) {
            this.input.setAttribute('aria-activedescendant', '');
            this.input.setAttribute('aria-expanded', false);
        }
        this.removeAttribute('loading');
        this.removeAttribute('open');
        const searchModalContainer = this.closest('.search-modal') || this.closest('.header_search_item') || this.parentElement;
        if (searchModalContainer) {
            searchModalContainer.classList.remove('searching');
        }
    }
    clear(event) {
        if (event) event.preventDefault();
        if (this.input) this.input.value = '';
        if (this.predictiveSearchResults) this.predictiveSearchResults.innerHTML = '';
        this.removeAttribute('open');
        this.removeAttribute('results');
        if (this.input) this.input.focus();
        const searchModalContainer = this.closest('.search-modal') || this.closest('.header_search_item') || this.parentElement;
        if (searchModalContainer) {
            searchModalContainer.classList.remove('searching');
        }
    }
}
customElements.define('predictive-search', PredictiveSearch);
